/**
 * Non-destructive party edit dialog.
 *
 * Edits: display_name, notes. Role changes go behind an Advanced section
 * because they're rare and the backend enforces role×assignment rules.
 * No amount, rate, or ledger-affecting fields.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import type { Party } from '../api';
import { Button, Checkbox, ErrorBanner, Select, Spinner, TextArea, TextInput } from '../ui/primitives';
import { Modal } from '../ui/Modal';

type Role = 'self' | 'upline' | 'client';

export interface EditPartyDialogProps {
  party: Party;
  onClose: () => void;
  onSaved: (updated: Party) => void;
}

export function EditPartyDialog({ party, onClose, onSaved }: EditPartyDialogProps) {
  const qc = useQueryClient();
  const [displayName, setDisplayName] = useState(party.displayName);
  const [notes, setNotes] = useState(party.notes ?? '');
  const [role, setRole] = useState<Role>(party.role as Role);
  const [showPoints, setShowPoints] = useState(party.showPoints);
  const [advanced, setAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () =>
      api.patch<Party>(`/api/parties/${party.id}`, {
        displayName: displayName.trim(),
        notes: notes.trim().length === 0 ? null : notes.trim(),
        showPoints,
        role,
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries();
      onSaved(updated);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!displayName.trim()) {
      setError('Display name is required.');
      return;
    }
    saveMut.mutate();
  };

  return (
    <Modal onClose={onClose}>
        <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
          <h2 className="text-small font-semibold text-ink">Edit party</h2>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </header>
        <form className="flex-1 overflow-y-auto p-4" onSubmit={onSubmit}>
          <div className="space-y-3">
            <TextInput
              label="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              name="display-name"
            />
            <TextArea
              label="Notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              hint="Metadata only — won't affect any amount or balance."
              placeholder="e.g. Preferred UPI 9876543210 via PhonePe"
            />

            <Checkbox
              label="Show points on exported statements"
              hint="Affects newly generated PDF/PNG statements only. On-screen ledger stays unchanged."
              checked={showPoints}
              onChange={setShowPoints}
            />

            <div>
              <button
                type="button"
                onClick={() => setAdvanced((v) => !v)}
                className="text-caption text-ink-muted hover:text-ink"
              >
                {advanced ? 'Hide advanced ▲' : 'Show advanced ▾'}
              </button>
              {advanced && (
                <div className="mt-2 space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <Select
                    label="Role"
                    value={role}
                    onChange={(v) => setRole(v as Role)}
                    options={[
                      { value: 'client', label: 'client' },
                      { value: 'upline', label: 'upline' },
                      { value: 'self', label: 'self' },
                    ]}
                  />
                  <p className="text-caption text-amber-600">
                    Role changes are rare. The server refuses changes that would break existing
                    assignments or ledger entries.
                  </p>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="mt-3">
              <ErrorBanner>{error}</ErrorBanner>
              {/* Rename collision → the message offers the merge path; link straight to it. */}
              {/already exists/i.test(error) && /merge/i.test(error) && (
                <p className="mt-1.5 text-caption">
                  <Link
                    to="/fix-problems"
                    className="font-medium text-sec-people underline underline-offset-2 hover:opacity-80"
                  >
                    Open Fix Problems to merge them →
                  </Link>
                </p>
              )}
            </div>
          )}

          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" disabled={saveMut.isPending}>
              {saveMut.isPending ? <Spinner /> : 'Save'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
    </Modal>
  );
}
